<script setup="">
import { PlusIcon, XIcon } from "lucide-vue-next";
import { computed, watch } from "vue";
import { object, string } from "yup";

import Alert from "@/Components/Alert.vue";
import Badge from "@/Components/Badge.vue";
import EmojiIcon from "@/Components/Form/EmojiIcon.vue";
import SimpleInput from "@/Components/Form/SimpleInput.vue";
import SimpleLabel from "@/Components/Form/SimpleLabel.vue";
import SimpleSelect from "@/Components/Form/SimpleSelect.vue";
import ConfirmModal from "@/Components/Modal/ConfirmModal.vue";
import SimpleButton from "@/Components/SimpleButton.vue";

import { useValidator } from "@/composables/validator";
import { useForm } from "@inertiajs/vue3";

const props = defineProps({
  campaign: {
    type: Object,
    required: true
  },
  update: {
    type: Function,
    required: true
  }
});

const actionButton = computed(() => props.campaign.settings.action);

const form = useForm({
  type: "link",
  tagline: "",
  title: "",
  value: "",
  target: "_self"
});

const schema = object().shape({
  type: string().required().label("타입"),
  tagline: string().nullable().label("윗 첨자"),
  title: string().required().label("버튼 타이틀"),
  value: string().max(512).required().label("링크"),
  target: string().nullable().label("링크 타입")
});

const validator = useValidator(schema);

const validate = () => validator.validateSync(form.data());

const types = [
  { label: "링크 이동", value: "link", default: true },
  { label: "전화 연결", value: "tel" }
];

const targets = [
  { label: "새 창", value: "_blank", default: true },
  { label: "현재 창", value: "_self" }
];

const reset = () => {
  form.reset();
  validator.reset();
};

const modify = () => {
  form.type = actionButton.value.type;
  form.tagline = actionButton.value.tagline;
  form.title = actionButton.value.title;
  form.value = actionButton.value.value;
};

const updateAction = (value) => {
  props.update("settings.action", value);
};

const confirmAndClose = ({ close }) => {
  updateAction(form.data());
  close();
};

const removeAction = () => {
  updateAction(null);
};

watch(
  () => form.type,
  () => (form.value = "")
);
</script>

<template>
  <div>
    <ConfirmModal v-if="!actionButton" title="플로팅 버튼 추가" class="flex-1" :validate="validate" @before-open="reset" @confirmed="confirmAndClose">
      <template #button="{ open }">
        <div>
          <SimpleButton classes="w-full text-sm+" @click="open">
            <template #icon>
              <PlusIcon class="mr-1 h-4 w-4" />
            </template>
            플로팅 버튼 추가
          </SimpleButton>
        </div>
      </template>

      <template #content>
        <div class="space-y-4">
          <div>
            <SimpleSelect v-model="form.type" label="버튼 타입" required :options="types" :error="form.errors.type" />
          </div>

          <div>
            <div class="mb-1 flex justify-between">
              <SimpleLabel>윗 첨자 (선택)</SimpleLabel>
              <div class="flex items-center space-x-1">
                <span class="text-xs+ font-semibold">이모티콘</span>
                <EmojiIcon @select="form.tagline = form.tagline + $event.i" />
              </div>
            </div>
            <SimpleInput v-model="form.tagline" max-length="24" placeholder="예) 더 자세히 알아 보려면" :error="validator.getFieldError('tagline')" />
          </div>

          <div>
            <div class="mb-1 flex justify-between">
              <SimpleLabel>버튼 타이틀</SimpleLabel>
              <div class="flex items-center space-x-1">
                <span class="text-xs+ font-semibold">이모티콘</span>
                <EmojiIcon @select="form.title = form.title + $event.i" />
              </div>
            </div>
            <SimpleInput v-model="form.title" max-length="16" placeholder="예) 홈페이지로 이동하기" :error="validator.getFieldError('title')" />
          </div>

          <div v-if="form.type === 'tel'">
            <SimpleInput v-model="form.value" label="전화 연결" placeholder="1577-0000 or 010-0000-0000" :error="form.errors.value" required />
          </div>

          <div v-if="form.type === 'link'">
            <SimpleInput
              v-model="form.value"
              :label="form.type === 'link' ? '버튼 링크' : '전화번호'"
              :placeholder="form.type === 'link' ? 'https://example.com' : '010-0000-0000'"
              :error="form.errors.value"
              required
              max-length="255"
            />
          </div>

          <div v-if="form.type === 'link'">
            <SimpleSelect v-model="form.target" label="링크 타입" required :options="targets" :error="form.errors.target" />
          </div>
        </div>
      </template>
    </ConfirmModal>
    <!--end::add-button-->

    <div class="mt-5">
      <ConfirmModal title="버튼 액션 수정" :validate="validate" @before-open="modify" @confirmed="confirmAndClose">
        <template #button="{ open }">
          <div
            v-if="!!actionButton"
            class="flex cursor-pointer items-center rounded-xl border px-4 py-3 text-sm hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
            @click="open"
          >
            <dl class="min-w-0 flex-1 overflow-hidden">
              <dt class="text-xs text-600">{{ actionButton?.tagline ?? "No tagline" }}</dt>
              <dd class="font-semibold text-800">{{ actionButton.title }}</dd>
              <dd class="mt-1 space-x-2 text-sm text-600">
                <Badge v-if="actionButton.type === 'link'" classes="py-0.5 text-500">링크</Badge>
                <Badge v-if="actionButton.type === 'tel'" classes="py-0.5 text-500">전화</Badge>
                <span class="text-xs+">{{ actionButton.value }}</span>
              </dd>
            </dl>
            <div class="ml-2 w-5 flex-none" @click.stop>
              <XIcon class="h-5 w-5 cursor-pointer transition text-600 hover:text-red-500" @click="removeAction" />
            </div>
          </div>
        </template>

        <template #content>
          <div class="space-y-4">
            <div>
              <SimpleSelect v-model="form.type" label="버튼 타입" required :options="types" :error="validator.getFieldError('type')" />
            </div>

            <div>
              <div class="mb-1 flex justify-between">
                <SimpleLabel>윗 첨자 (선택)</SimpleLabel>
                <div class="flex items-center space-x-1">
                  <span class="text-xs+ font-semibold">이모티콘</span>
                  <EmojiIcon @select="form.tagline = form.tagline + $event.i" />
                </div>
              </div>
              <SimpleInput
                v-model="form.tagline"
                max-length="24"
                placeholder="예) 더 자세히 알아 보려면"
                :error="validator.getFieldError('tagline')"
              />
            </div>

            <div>
              <div class="mb-1 flex justify-between">
                <SimpleLabel>버튼 타이틀</SimpleLabel>
                <div class="flex items-center space-x-1">
                  <span class="text-xs+ font-semibold">이모티콘</span>
                  <EmojiIcon @select="form.title = form.title + $event.i" />
                </div>
              </div>
              <SimpleInput v-model="form.title" placeholder="예) 고객센터" :error="validator.getFieldError('title')" />
            </div>

            <div v-if="form.type === 'tel'">
              <SimpleInput
                v-model="form.value"
                label="전화 연결"
                placeholder="1577-0000 or 010-0000-0000"
                :error="validator.getFieldError('value')"
                required
              />
            </div>

            <div v-if="form.type === 'link'">
              <SimpleInput
                v-model="form.value"
                :label="form.type === 'link' ? '버튼 링크' : '전화번호'"
                :placeholder="form.type === 'link' ? 'https://example.com' : '010-0000-0000'"
                :error="validator.getFieldError('value')"
                required
                max-length="255"
              />
            </div>

            <div v-if="form.type === 'link'">
              <SimpleSelect v-model="form.target" label="링크 타입" required :options="targets" :error="validator.getFieldError('target')" />
            </div>
          </div>
        </template>
      </ConfirmModal>

      <div v-if="!actionButton">
        <Alert classes="text-center text-sm text-600">플로팅 버튼이 없습니다.</Alert>
      </div>
      <!--end::empty-state-->
    </div>
  </div>
</template>
